import { useState, useRef, useCallback } from 'react';
import { dashboardHandler, CustomDateParams, createCancelSource, isRequestCancelled, CancelTokenSource } from '../DashboardHandler';
import { mapTopPerformersByModeResponse } from '../utils/mappers';
import type { Performer, CustomDateRange } from '../types';
import { getComparisonDays } from '../Components/DateRangePicker';

interface UseTopPerformersByModeReturn {
  performers: Performer[];
  loading: boolean;
  error: string | null;
  fetchTopPerformers: (mode: string, period: string, customRange?: CustomDateRange) => void;
  reset: () => void;
}

export const useTopPerformersByMode = (): UseTopPerformersByModeReturn => {
  const [performers, setPerformers] = useState<Performer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cancelSourceRef = useRef<CancelTokenSource | null>(null);

  const fetchTopPerformers = useCallback((mode: string, period: string, customRange?: CustomDateRange) => {
    // Cancel any in-flight request
    if (cancelSourceRef.current) {
      cancelSourceRef.current.cancel('New request started');
    }

    const source = createCancelSource();
    cancelSourceRef.current = source;

    setLoading(true);
    setError(null);

    let customDate: CustomDateParams | undefined;
    if (period === 'custom' && customRange) {
      customDate = {
        startDate: customRange.startDate,
        endDate: customRange.endDate,
        comparisonDays: getComparisonDays(customRange),
      };
    }

    dashboardHandler
      .getTopPerformersByMode(mode, period, source.token, customDate)
      .then((result) => {
        setPerformers(mapTopPerformersByModeResponse(result));
        setLoading(false);
      })
      .catch((err) => {
        if (!isRequestCancelled(err)) {
          console.error('Failed to fetch top performers by mode:', err);
          setError(err.message || 'Failed to load top performers');
          setLoading(false);
        }
      });
  }, []);

  const reset = useCallback(() => {
    if (cancelSourceRef.current) {
      cancelSourceRef.current.cancel('Modal closed');
    }
    setPerformers([]);
    setLoading(false);
    setError(null);
  }, []);

  return { performers, loading, error, fetchTopPerformers, reset };
};
